// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useEffect } from 'react';

import {
  insightsStateTypeNames,
  type ChartDataPoints,
  type DashboardFilterToApi,
  useEvolvePost,
  ignoreCanceledApiRequest,
} from '@evolvemep/api-ts';
import { Stack, Card, LoadingOverlay } from '@mantine/core';
import type { AgCartesianSeriesOptions, AgChartOptions } from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';

import { isKeyOf } from 'helpers/isKeyOf';
import { isNotNil } from 'helpers/isNotNil';
import { InsightsStats } from 'modules/Shop/Insights/ShopInsights/InsightsStats';

import { statusToChartColorMap } from './constants';

const InsightsChartWrapped = ({ chartDataPoints }: { chartDataPoints: ChartDataPoints[] }) => {
  const series = useMemo<AgCartesianSeriesOptions[]>(
    () =>
      insightsStateTypeNames.map((key) => ({
        type: 'bar',
        xKey: 'Month',
        yKey: key,
        yName: key,
        stacked: true,
        cornerRadius: 5,
        fill:
          // Only non-late statuses have custom colors...
          isKeyOf(key, statusToChartColorMap)
            ? statusToChartColorMap[key]
            : // ...so default to the "Late" color for all others
              statusToChartColorMap.Late,
        showInLegend: chartDataPoints.some((entry) => isKeyOf(key, entry)),
        tooltip: {
          // TODO: type this better
          renderer: ({ datum, xKey, yKey }) => ({
            // oxlint-disable-next-line typescript/no-unsafe-member-access
            title: datum[xKey],
            // oxlint-disable-next-line typescript/no-unsafe-call, typescript/no-unsafe-member-access
            content: `${yKey}: ${datum[yKey].toFixed(0)}`,
          }),
        },
      })),
    [chartDataPoints],
  );
  const options = useMemo<AgChartOptions>(
    () => ({
      title: {
        text: 'Work Orders Over Time',
        color: 'black',
        fontSize: 20,
        fontFamily: 'IBM Plex Sans',
      },
      data: chartDataPoints,
      series,
      overlays: {
        noData: {
          enabled: chartDataPoints.length === 0,
        },
      },
      axes: {
        y: {
          type: 'number',
          interval: {
            step: 5,
          },
          position: 'left',
        },
        x: {
          type: 'category',
          position: 'bottom',
        },
      },
      legend: {
        position: 'bottom',
        item: {
          marker: {
            shape: 'circle',
          },
        },
      },
    }),
    [chartDataPoints, series],
  );

  return <AgCharts options={options} style={{ height: '100%' }} />;
};

type Props = {
  params: DashboardFilterToApi;
};

export const InsightsChart = ({ params }: Props) => {
  const { data, apiCall, loading } = useEvolvePost('shop/insights/workOrdersOverTime', {
    abortOnMoreRecent: true,
    abortOnUnmount: true,
  });

  useEffect(() => {
    void apiCall(params).catch(ignoreCanceledApiRequest);
  }, [apiCall, params]);

  return (
    <Stack gap={0} h="100%" pos="relative">
      <InsightsStats data={data} params={params} />
      <Card withBorder style={{ height: '100%', backgroundColor: 'white' }}>
        {isNotNil(data) && !loading && <InsightsChartWrapped chartDataPoints={data.chartDataPoints} />}
      </Card>
      <LoadingOverlay visible={loading} />
    </Stack>
  );
};
